/**
 * Filtros del mapa de calor y su traducción a parámetros de la API.
 *
 * Es lógica pura y sin React a propósito: es lo que se puede probar sin montar
 * un canvas WebGL, y es donde vive la única regla del módulo que no es
 * cosmética — **`minScans` se puede subir, nunca bajar de 3**.
 *
 * Ese 3 es `MIN_CELL_SCANS` del backend (`services/api/analytics/queries.py`):
 * k-anonimato. Una celda de 1,2 km con un solo escaneo es una persona en un
 * lugar y en un momento, y en farma eso permite inferir su condición de salud
 * (regla 5 del dominio). El servidor lo rechaza igual con un 422; acá se
 * bloquea antes para que la UI no ofrezca nunca un control que produzca un
 * error, ni sugiera que bajarlo sea una opción.
 */
import type { HeatmapKeyFilters } from '@/api/keys';
import type { QueryParams } from '@/api/types';
import type { MessageKey } from '@/i18n/messages';

/**
 * Capas que acepta `GET /api/v1/analytics/heatmap`. Son las de `_LAYERS` en
 * `services/api/routers/analytics.py`; mandar otra devuelve 400.
 *
 * `already_seen` no está y no es un olvido: un consumidor que escanea dos
 * veces su propia caja es lo más común del mundo, y pintarlo de rojo entrena
 * al equipo a ignorar el mapa.
 */
export const MAP_LAYERS = [
  'all',
  'genuine',
  'suspicious',
  'not_activated',
  'stolen',
  'recalled',
  'out_of_territory',
] as const;

export type MapLayer = (typeof MAP_LAYERS)[number];

export const LAYER_LABEL_KEYS: Record<MapLayer, MessageKey> = {
  all: 'map.layer.all',
  genuine: 'map.layer.genuine',
  suspicious: 'map.layer.suspicious',
  not_activated: 'map.layer.notActivated',
  stolen: 'map.layer.stolen',
  recalled: 'map.layer.recalled',
  out_of_territory: 'map.layer.outOfTerritory',
};

/** Las capas que ya son sospecha: sobre ellas la superposición no aporta. */
const ALERT_LAYERS = new Set<MapLayer>([
  'suspicious',
  'not_activated',
  'stolen',
  'recalled',
  'out_of_territory',
]);

export function isAlertLayer(layer: MapLayer): boolean {
  return ALERT_LAYERS.has(layer);
}

// ────────────────────────────────────────────────────────────── los límites

/** `MIN_CELL_SCANS` del backend. No es un default: es un piso. */
export const MIN_SCANS_FLOOR = 3;
export const MAX_SCANS_THRESHOLD = 10_000;

/** `geohash.MAX_PRECISION`. A precisión 8 una celda es una casa. */
export const MAX_PRECISION = 6;
export const MIN_PRECISION = 1;

/** `MAX_DAYS` del backend. */
export const MAX_DAYS = 400;

/** `MAX_CELLS` del backend. Un mapa no dibuja más que esto. */
export const MAX_CELLS = 5_000;

/** Períodos ofrecidos. El último es el rango de doce meses del informe. */
export const PERIOD_DAYS = [7, 30, 90, 180, 365] as const;

/** Precisiones ofrecidas. Sin la 1 y la 2: a esa escala todo es una mancha. */
export const PRECISION_LEVELS = [3, 4, 5, 6] as const;

export type PrecisionLevel = (typeof PRECISION_LEVELS)[number];

/** Cada nivel se nombra por el tamaño de celda, no por el número de geohash. */
export const PRECISION_LABEL_KEYS: Record<PrecisionLevel, MessageKey> = {
  3: 'map.precision.3',
  4: 'map.precision.4',
  5: 'map.precision.5',
  6: 'map.precision.6',
};

export interface MapFilters extends HeatmapKeyFilters {
  layer: MapLayer;
  /** Dibujar encima las celdas con resultado sospechoso. */
  alertOverlay: boolean;
}

export const DEFAULT_MAP_FILTERS: MapFilters = {
  days: 90,
  // 4 (19 × 39 km) y no 5 (4,9 km): la vista inicial abarca Sudamérica entera,
  // y a ese zoom una celda de 4,9 km mide uno o dos píxeles — el mapa se veía
  // salpicado de puntos sueltos en vez de mostrar concentración. La precisión
  // se sube desde el filtro al acercarse. Atarla al zoom automáticamente es
  // #131.
  precision: 4,
  layer: 'all',
  minScans: MIN_SCANS_FLOOR,
  limit: 2_000,
  alertOverlay: true,
};

function clampInt(value: number, min: number, max: number, fallback: number): number {
  if (!Number.isFinite(value)) return fallback;
  return Math.min(max, Math.max(min, Math.trunc(value)));
}

/**
 * Deja los filtros dentro de lo que la API acepta. Se aplica en cada cambio,
 * no sólo al construir: un control roto o una URL pegada a mano no deben
 * poder pedir una celda por debajo del umbral de supresión.
 */
export function clampFilters(filters: MapFilters): MapFilters {
  return {
    days: clampInt(filters.days, 1, MAX_DAYS, DEFAULT_MAP_FILTERS.days),
    precision: clampInt(
      filters.precision,
      MIN_PRECISION,
      MAX_PRECISION,
      DEFAULT_MAP_FILTERS.precision,
    ),
    layer: MAP_LAYERS.includes(filters.layer) ? filters.layer : DEFAULT_MAP_FILTERS.layer,
    minScans: clampInt(
      filters.minScans,
      MIN_SCANS_FLOOR,
      MAX_SCANS_THRESHOLD,
      MIN_SCANS_FLOOR,
    ),
    limit: clampInt(filters.limit, 1, MAX_CELLS, DEFAULT_MAP_FILTERS.limit),
    alertOverlay: filters.alertOverlay,
  };
}

/** Sólo lo que va en la clave de cache: `alertOverlay` no viaja a la API. */
export function heatmapKeyOf(filters: MapFilters): HeatmapKeyFilters {
  return {
    days: filters.days,
    precision: filters.precision,
    layer: filters.layer,
    minScans: filters.minScans,
    limit: filters.limit,
  };
}

/** Parámetros de `GET /api/v1/analytics/heatmap`. */
export function toHeatmapQuery(filters: HeatmapKeyFilters): QueryParams {
  return {
    days: filters.days,
    precision: filters.precision,
    layer: filters.layer,
    min_scans: filters.minScans,
    limit: filters.limit,
  };
}
